'use client';

import { Box } from '@mui/material';
import { Ilr2StructuredImgCell } from '@/components/publicSearch/Ilr2StructuredImgCell';
import { parseMixedIlr2Html } from '@/utils/stripHtmlForTooltip';

type Ilr2MixedHtmlLabelProps = {
  raw: string | null | undefined;
  societyId: string | number | undefined;
  /** Receives stripped plain text (never raw HTML). */
  children: (plainText: string) => React.ReactNode;
  badgeAlt?: string;
  /** When true, keeps caption text and badge on one line (grid breed-average row). */
  nowrap?: boolean;
};

/**
 * Renders ILR2 mixed markup: plain text (via `children`) plus an optional
 * img badge (HERF trait leader, completeness icon, etc.).
 */
export function Ilr2MixedHtmlLabel({
  raw,
  societyId,
  children,
  badgeAlt = 'Badge',
  nowrap = false,
}: Ilr2MixedHtmlLabelProps) {
  const { plainText, imgMarkup } = parseMixedIlr2Html(raw);
  const showBadge = Boolean(imgMarkup && societyId);

  if (!plainText && !showBadge) {
    return null;
  }

  return (
    <Box
      component="span"
      sx={{
        display: nowrap ? 'inline' : 'inline-flex',
        alignItems: 'center',
        gap: nowrap ? undefined : 0.5,
        minWidth: 0,
        maxWidth: '100%',
        flexWrap: nowrap ? 'nowrap' : 'wrap',
      }}
    >
      {children(plainText)}
      {showBadge ? (
        <Ilr2StructuredImgCell
          value={imgMarkup!}
          societyId={societyId!}
          alt={badgeAlt}
        />
      ) : null}
    </Box>
  );
}
